import AppCard from '@nxsys/components/AppCard';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppDescription from '@nxsys/components/AppDescription';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { EeErPensionMethodEnum } from '@nxsys/constants/AppEnums';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppRadioReversed from '@nxsys/components/AppRadioReversed';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { CompanyCreationTypeEnum } from '@nxsys/constants/AppEnums';
import { Option } from '@nxsys/types/models/Common';
import { Button, Col, Descriptions, Divider, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import clsx from 'clsx';
import moment, { Moment } from 'moment';
import { Ref, useImperativeHandle, useMemo } from 'react';
import IconWrapper from './IconWrapper';
import StepTitle from './StepTitle';
import { getTypeOfValueOptions } from '@nxsys/constants/AppConstants';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { showCurrencyByCountry } from '@nxsys/utils';
import {
  autoEnrolmentSchemeTypeIrelandOptions,
  privatePensionSchemeTypeOptions,
} from '@nxsys/constants/AppConst';
import { orderBy } from 'lodash';
import { getRangeYear } from '@nxsys/utils/taxYear';
import { IRELAND } from '@nxsys/store/constants/keys';
import { formatPostCode, postCodeValidationRule } from '@nxsys/utils/formFieldValidation';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

export type IrelandPensionSchemeFormValues = {
  type: Option<string> | null;
  name: string;
  reference: string;
  eePensionValue: number | null;
  eePensionType: Option<string> | null;
  erPensionValue: number | null;
  erPensionType: Option<string> | null;
  avcPensionValue: number | null;
  avcPensionType: Option<string> | null;
  isDefault: boolean;
};

export type PensionValues = {
  // UK fields
  pensionStatement: string;
  nextReEnrolmentDate: Moment | null;
  stagingDate: Moment | null;
  // Ireland fields
  irelandPrivatePensionSchemes: IrelandPensionSchemeFormValues[];
  // Ireland Contact & Payment fields
  contactPaymentSameAsEmployer: boolean;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  eircode: string;
  phone: string;
  bankName: string;
  bankBranch: string;
  bankSortCode: string;
  accountNumber: string;
  bankAccountName: string;
  bankReference: string;
  iban: string;
  bic: string;
};

export type PensionFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => PensionValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<PensionFormRef>;
  initialValues: Partial<PensionValues>;
  isIreland?: boolean;
  stepNumber: number;
  creationType?: CompanyCreationTypeEnum;
};

export enum PensionStatement {
  EMPLOYING_BEFORE = 'EMPLOYING_BEFORE',
  FIRST_STAFF_THIS_YEAR = 'FIRST_STAFF_THIS_YEAR',
  EXEMPT = 'EXEMPT',
}

const formatTaxYear = (value: string, isIreland: boolean) => {
  const [start, end] = value.split('-');
  return isIreland ? start : `${start}/${end.slice(-2)}`;
};

const Pension: React.FC<Props> = ({
  formRef,
  initialValues,
  isIreland,
  stepNumber,
  creationType,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const taxYearRange = orderBy(getRangeYear(!!isIreland), 'value', 'desc');
  const currentTaxYearOption = taxYearRange[0];
  const taxYearLabel = formatTaxYear(currentTaxYearOption?.label, !!isIreland);

  const pensionTypeOptions = getTypeOfValueOptions(
    showCurrencyByCountry(payrollCountry),
  );

  // EMPLOYING_BEFORE is only allowed for CONTINUE_PARTWAY
  const isEmployingBeforeAllowed =
    creationType === CompanyCreationTypeEnum.CONTINUE_PARTWAY;

  const options = [
    ...(isEmployingBeforeAllowed
      ? [
          {
            label: `I have been employing staff since on or before the ${taxYearLabel} tax year`,
            value: PensionStatement.EMPLOYING_BEFORE,
          },
        ]
      : []),
    {
      label: `My first staff member will start in the ${taxYearLabel} tax year`,
      value: PensionStatement.FIRST_STAFF_THIS_YEAR,
    },
    {
      label: 'I am exempt from Auto Enrolment',
      value: PensionStatement.EXEMPT,
    },
  ];

  const [form] = Form.useForm<PensionValues>();
  const watchedSchemes = Form.useWatch('irelandPrivatePensionSchemes', form);
  const watchValues = Form.useWatch([], form);
  const selectedOption = watchValues?.pensionStatement;
  const stagingDateValue = watchValues?.stagingDate as Moment | null;

  const reEnrolmentDateRange = useMemo(() => {
    if (!stagingDateValue || !moment.isMoment(stagingDateValue)) return null;
    const end = moment(stagingDateValue).add(3, 'years').subtract(1, 'day');
    const start = moment(end).subtract(3, 'months');
    return { start, end };
  }, [stagingDateValue]);
  const handleDefaultIrelandSchemeChange = (
    index: number,
    checked: boolean,
  ) => {
    const schemes =
      (form.getFieldValue(
        'irelandPrivatePensionSchemes',
      ) as IrelandPensionSchemeFormValues[]) || [];
    const updatedSchemes = schemes.map((scheme, i) => ({
      ...scheme,
      isDefault: checked && i === index,
    }));
    form.setFieldsValue({ irelandPrivatePensionSchemes: updatedSchemes });
  };

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  if (isIreland) {
    return (
      <div className="Pension">
        <StepTitle step={stepNumber} title="Pension" description="" />

        <div className="mt-[24px]">
          <AppFormContainer
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            initialValues={{
              irelandPrivatePensionSchemes: [],
              contactPaymentSameAsEmployer: true,
              ...initialValues,
            }}
            layout="vertical"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            {/* Auto Enrolment Section - Ireland: read-only NAERSA info (matching production) */}
            <div className="mb-[24px]">
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconMoneyBill" />
                <div className="text-[16px] font-semibold">
                  {messages['common.autoEnrolment'] || 'Auto Enrolment'}
                </div>
              </div>
              <div className="text-[14px] text-neutral-600 mb-[16px]">
                Ireland auto enrolment pension settings managed by NAERSA.
              </div>

              <AppDescription
                column={1}
                labelStyle={{ width: 316 }}
                bordered={false}
                colon={false}
                size="middle"
              >
                <Descriptions.Item
                  label={
                    messages['common.typeOfScheme'] || 'Type of Scheme'
                  }
                >
                  {autoEnrolmentSchemeTypeIrelandOptions[0]?.label || '-'}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    messages['common.defaultEmployeeContribution'] ||
                    'Default Employee Contribution'
                  }
                >
                  {'1.5%'}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    messages['common.defaultEmployerContribution'] ||
                    'Default Employer Contribution'
                  }
                >
                  {'1.5%'}
                </Descriptions.Item>
              </AppDescription>
            </div>

            <Divider />

            {/* Private Pension Schemes Section */}
            <div className="mb-[24px]">
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconMoneyBill" />
                <div className="text-[16px] font-semibold">
                  Private Pension Schemes
                </div>
              </div>
              <div className="text-[14px] text-neutral-600 mb-[16px]">
                Configure private pension schemes (PRSA, RBS, RAC, PEPP) for
                your employees.
              </div>

              <Form.List name="irelandPrivatePensionSchemes">
                {(fields, { add, remove }) => (
                  <div className="space-y-[16px]">
                    {fields.map((field, index) => (
                      <AppCard key={field.key} className="p-[16px]">
                        <div className="flex items-center justify-between mb-[16px]">
                          <div className="text-[14px] font-semibold">
                            Scheme {index + 1}
                          </div>
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(field.name)}
                            size="small"
                          >
                            Remove
                          </Button>
                        </div>
                        <Row gutter={16}>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Scheme Type"
                              name={[field.name, 'type']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please select scheme type',
                                },
                              ]}
                            >
                              <AppSelect
                                placeholder="Select scheme type"
                                options={privatePensionSchemeTypeOptions}
                              />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Scheme Name"
                              name={[field.name, 'name']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Please enter scheme name',
                                },
                              ]}
                            >
                              <AppInput placeholder="Enter scheme name" />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Scheme Reference"
                              name={[field.name, 'reference']}
                            >
                              <AppInput placeholder="Enter reference" />
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label={
                                messages['common.defaultEmployeeContribution']
                              }
                            >
                              <Row gutter={8}>
                                <Col span={6}>
                                  <Form.Item
                                    name={[field.name, 'eePensionType']}
                                    noStyle
                                  >
                                    <AppSelect
                                      options={pensionTypeOptions}
                                      className="w-full !min-w-0"
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={18}>
                                  <Form.Item
                                    name={[field.name, 'eePensionValue']}
                                    noStyle
                                  >
                                    {watchedSchemes?.[field.name]?.eePensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                                      <AppCurrencyInput
                                        placeholder={
                                          messages['common.enterAmount']
                                        }
                                      />
                                    ) : (
                                      <AppRateInput
                                        placeholder={
                                          messages['common.enterAmount']
                                        }
                                      />
                                    )}
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label={
                                messages['common.defaultEmployerContribution']
                              }
                            >
                              <Row gutter={8}>
                                <Col span={6}>
                                  <Form.Item
                                    name={[field.name, 'erPensionType']}
                                    noStyle
                                  >
                                    <AppSelect
                                      options={pensionTypeOptions}
                                      className="w-full !min-w-0"
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={18}>
                                  <Form.Item
                                    name={[field.name, 'erPensionValue']}
                                    noStyle
                                  >
                                    {watchedSchemes?.[field.name]?.erPensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                                      <AppCurrencyInput
                                        placeholder={
                                          messages['common.enterAmount']
                                        }
                                      />
                                    ) : (
                                      <AppRateInput
                                        placeholder={
                                          messages['common.enterAmount']
                                        }
                                      />
                                    )}
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item label="AVC Contribution Value">
                              <Row gutter={8}>
                                <Col span={6}>
                                  <Form.Item
                                    name={[field.name, 'avcPensionType']}
                                    noStyle
                                  >
                                    <AppSelect
                                      options={pensionTypeOptions}
                                      className="w-full !min-w-0"
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={18}>
                                  <Form.Item
                                    name={[field.name, 'avcPensionValue']}
                                    noStyle
                                  >
                                    {watchedSchemes?.[field.name]?.avcPensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                                      <AppCurrencyInput
                                        placeholder={
                                          messages['common.enterAmount']
                                        }
                                      />
                                    ) : (
                                      <AppRateInput
                                        placeholder={
                                          messages['common.enterAmount']
                                        }
                                      />
                                    )}
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Form.Item>
                          </Col>
                          <Col sm={24} md={12}>
                            <Form.Item
                              label="Enabled"
                              name={[field.name, 'isDefault']}
                              valuePropName="checked"
                            >
                              <AppSwitch
                                onChange={(checked) =>
                                  handleDefaultIrelandSchemeChange(
                                    index,
                                    checked,
                                  )
                                }
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </AppCard>
                    ))}
                    <Button type="dashed" onClick={() => add()} block>
                      + Add Pension Scheme
                    </Button>
                  </div>
                )}
              </Form.List>
            </div>

            <Divider />

            {/* Contact & Payment Section */}
            <div className="mb-[24px]">
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconMoneyBill" />
                <div className="text-[16px] font-semibold">
                  {messages['common.contactAndPayment'] || 'Contact & Payment'}
                </div>
              </div>
              <div className="text-[14px] text-neutral-600 mb-[16px]">
                Enter contact details and bank payment information for pension
                contributions.
              </div>

              <Form.Item
                name="contactPaymentSameAsEmployer"
                valuePropName="checked"
                className="mb-[16px]"
              >
                <AppCheckbox>
                  Same as Employer detail and bank payment information
                </AppCheckbox>
              </Form.Item>

              {!watchValues?.contactPaymentSameAsEmployer && (
              <Row gutter={24}>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.address1']}
                    name="address1"
                  >
                    <AppInput placeholder={messages['common.addressLine1']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.address2']}
                    name="address2"
                  >
                    <AppInput placeholder={messages['common.addressLine2']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.address3']}
                    name="address3"
                  >
                    <AppInput placeholder={messages['common.addressLine3']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.address4']}
                    name="address4"
                  >
                    <AppInput placeholder={messages['common.addressLine4']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={
                      payrollCountry === IRELAND
                        ? messages['common.eircode']
                        : messages['common.postCode']
                    }
                    name="eircode"
                    rules={[postCodeValidationRule(messages, payrollCountry)]}
                  >
                    <AppInput
                      placeholder={
                        payrollCountry === IRELAND
                          ? messages['common.eircode']
                          : messages['common.postCode']
                      }
                      upperCaseAll
                      onChange={(e) => {
                        form.setFieldValue(
                          'eircode',
                          formatPostCode(e.target.value, payrollCountry),
                        );
                      }}
                      maxLength={8}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item label={messages['common.phone']} name="phone">
                    <AppInput placeholder={messages['common.phone']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.bankName']}
                    name="bankName"
                  >
                    <AppInput placeholder={messages['common.bankName']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.bankBranch']}
                    name="bankBranch"
                  >
                    <AppInput placeholder={messages['common.bankBranch']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.bankSortCode']}
                    name="bankSortCode"
                  >
                    <AppInput placeholder={messages['common.bankSortCode']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.accountNumber']}
                    name="accountNumber"
                  >
                    <AppInput placeholder={messages['common.accountNumber']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.bankAccountName']}
                    name="bankAccountName"
                  >
                    <AppInput
                      placeholder={messages['common.bankAccountName']}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.bankReference']}
                    name="bankReference"
                  >
                    <AppInput placeholder={messages['common.bankReference']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item label={messages['common.iban']} name="iban">
                    <AppInput placeholder={messages['common.iban']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item label={messages['common.bic']} name="bic">
                    <AppInput placeholder={messages['common.bic']} />
                  </Form.Item>
                </Col>
              </Row>
              )}
            </div>
          </AppFormContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="Pension">
      <StepTitle step={stepNumber} title="Pension" description="" />

      <div className="text-[14px] text-neutral-600 mt-[24px] mb-[24px]">
        Under the Pensions Act 2008, every employer in the UK must put certain
        staff into a workplace pension scheme and contribute towards it. This
        must be done from your duties stat date onwards.
      </div>

      <div className="space-y-[16px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            pensionStatement: PensionStatement.EMPLOYING_BEFORE,
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <div className="mb-[16px]">
            <div className="text-[14px] text-primary-600 italic mb-[24px]">
              *Choose which statement below applies to you
            </div>
            <Form.Item name="pensionStatement">
              <AppRadioGroup className="flex flex-col gap-y-[12px]">
                {options.map((option, index) => (
                  <div
                    key={option.value}
                    className={clsx(
                      'border border-neutral-200 rounded-[12px] p-[16px]',
                      {
                        'border-primary-500': option.value === selectedOption,
                      },
                    )}
                  >
                    <AppRadioReversed value={option.value}>
                      <div className="bg-warning-50 border border-warning-300 px-[10px] py-[2px] w-fit text-warning-700 text-[14px] rounded-full mb-[12px]">{`Options ${index + 1}`}</div>
                      <div className="text-[16px]">{option.label}</div>
                    </AppRadioReversed>
                  </div>
                ))}
              </AppRadioGroup>
            </Form.Item>
          </div>

          {selectedOption && (
            <AppCard className="p-[16px] mb-[16px]">
              <div className="flex items-start gap-x-[12px]">
                <img
                  src="/assets/images/pig-money.png"
                  alt="pension"
                  className="w-[80px] h-[80px]"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-x-[8px] bg-neutral-50 border border-neutral-200 px-[7px] py-[2px] w-fit rounded-full mb-[12px]">
                    <div className="w-[8px] h-[8px] bg-success-500 rounded-full" />
                    <div className="text-[12px]">
                      Option{' '}
                      {options.findIndex(
                        (option) => option.value === selectedOption,
                      ) + 1}
                    </div>
                  </div>
                  <div className="text-[14px] text-neutral-600">
                    {selectedOption === PensionStatement.EMPLOYING_BEFORE
                      ? 'Your duties will continue from the start of the tax year.'
                      : selectedOption ===
                          PensionStatement.FIRST_STAFF_THIS_YEAR
                        ? 'Your duties start date is when your first member of staff starts'
                        : 'You are exempt from Auto Enrolment'}
                  </div>
                </div>
              </div>
            </AppCard>
          )}

          {isEmployingBeforeAllowed &&
            selectedOption === PensionStatement.EMPLOYING_BEFORE && (
              <>
                <div className="text-[14px] text-neutral-600 mb-[16px]">
                  Every three years, you must re-assess your staff, re-enrol
                  eligible employees, and submit a re-declaration of compliance.
                  You can choose a re-enrolment date within 3 months before the
                  third anniversary of your last re-enrolment or duties start
                  date.
                </div>

                <Divider variant="dashed" />

                <div className="text-[14px] font-[400] text-neutral-500 mb-[16px] italic">
                  *Enter or select your original automatic enrolment staging
                  date (i.e. that which was provided to you by The Pensions
                  Regulator, up to 1 February 2018), your duties start date
                  (i.e. when you first employed a member of staff on or after 1
                  October 2017), or your most recent automatic re-enrolment
                  date.
                </div>

                <Form.Item
                  name="stagingDate"
                  label="Staging date, duties start date, or previous re-enrolment date"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a date',
                    },
                  ]}
                >
                  <AppDatePicker
                    placeholder="Select date"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>

                <div className="text-[14px] font-[400] text-neutral-500 mb-[16px] italic">
                  {reEnrolmentDateRange
                    ? `*Your next re-enrolment date should be a date of your choosing between ${reEnrolmentDateRange.start.format('D MMMM YYYY')} and ${reEnrolmentDateRange.end.format('D MMMM YYYY')} (i.e. three months before the third anniversary of the date you entered above)`
                    : '*Your next re-enrolment date should be a date of your choosing within 3 months before the third anniversary of the date you entered above.'}
                </div>

                <Form.Item
                  name="nextReEnrolmentDate"
                  label="Next re-enrolment date"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a re-enrolment date',
                    },
                  ]}
                >
                  <AppDatePicker
                    placeholder="Select date"
                    format="DD/MM/YYYY"
                    disabledDate={
                      reEnrolmentDateRange
                        ? (current: Moment) =>
                            current.isBefore(reEnrolmentDateRange.start, 'day') ||
                            current.isAfter(reEnrolmentDateRange.end, 'day')
                        : undefined
                    }
                  />
                </Form.Item>
              </>
            )}
        </AppFormContainer>
      </div>
    </div>
  );
};

export default Pension;
